export const parseDateLike = (value: Date | string | number | null | undefined): Date | null => {
    if (!value) return null;

    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }

    if (typeof value === "number") {
        const asDate = new Date(value);
        return Number.isNaN(asDate.getTime()) ? null : asDate;
    }

    if (typeof value === "string") {
        const trimmed = value.trim();
        if (!trimmed) {
            return null;
        }

        // Prefer ISO-like parsing; replace first space with "T" when no explicit separator exists.
        const hasTSeparator = trimmed.includes("T");
        const normalized = hasTSeparator ? trimmed : trimmed.replace(" ", "T");
        const parsed = new Date(normalized);
        if (!Number.isNaN(parsed.getTime())) {
            return parsed;
        }

        const fallback = new Date(trimmed);
        if (!Number.isNaN(fallback.getTime())) {
            return fallback;
        }
    }

    return null;
};

interface Game {
    appointment_date?: string | Date;
    date?: string | Date;
    match_date?: string | Date;
    start_date?: string | Date;
    scheduled_date?: string | Date;
    appointment_start_time?: string;
    start_time?: string;
    kickoff_time?: string;
    kick_off_time?: string;
    time?: string;
    match_time?: string;
    appointment_status?: string;
    status?: string;
    joined_status?: string;
    state?: string;
    match_status?: string;
    game_status?: string;
    [key: string]: unknown;
}

export const combineDateAndTime = (
    dateValue: unknown,
    timeValue: unknown,
    { fallbackToEndOfDay = true } = {}
): Date | null => {
    const baseDate = parseDateLike(dateValue as Date | string | number);
    if (!baseDate) return null;

    const resolvedBase = new Date(baseDate);

    if (!timeValue) {
        if (fallbackToEndOfDay) {
            resolvedBase.setHours(23, 59, 59, 999);
        }
        return resolvedBase;
    }

    const timeString = String(timeValue).trim();
    if (!timeString) {
        if (fallbackToEndOfDay) {
            resolvedBase.setHours(23, 59, 59, 999);
        }
        return resolvedBase;
    }

    const isoDateString = [
        resolvedBase.getFullYear(),
        String(resolvedBase.getMonth() + 1).padStart(2, "0"),
        String(resolvedBase.getDate()).padStart(2, "0"),
    ].join("-");

    const candidateStrings = [];
    candidateStrings.push(`${isoDateString}T${timeString}`);

    if (typeof dateValue === "string") {
        candidateStrings.push(`${dateValue}T${timeString}`);
        candidateStrings.push(`${dateValue} ${timeString}`);
    }

    if (
        timeString.includes("T") ||
        timeString.includes("Z") ||
        timeString.includes("+")
    ) {
        candidateStrings.push(timeString);
    }

    for (const candidate of candidateStrings) {
        const parsedCandidate = parseDateLike(candidate);
        if (parsedCandidate) {
            return parsedCandidate;
        }
    }

    const timeMatch = timeString.match(
        /^(\d{1,2})(?::(\d{2}))?(?::(\d{2}))?(?:\.(\d+))?\s*(AM|PM)?$/i
    );

    if (timeMatch) {
        let hours = parseInt(timeMatch[1] ?? "0", 10);
        const minutes = parseInt(timeMatch[2] ?? "0", 10);
        const seconds = parseInt(timeMatch[3] ?? "0", 10);
        const meridiem = timeMatch[5] ? timeMatch[5].toUpperCase() : null;

        if (meridiem === "PM" && hours < 12) {
            hours += 12;
        } else if (meridiem === "AM" && hours === 12) {
            hours = 0;
        }

        const withTime = new Date(resolvedBase);
        withTime.setHours(
            Number.isFinite(hours) ? hours : 0,
            Number.isFinite(minutes) ? minutes : 0,
            Number.isFinite(seconds) ? seconds : 0,
            0
        );
        return withTime;
    }

    if (fallbackToEndOfDay) {
        const endOfDay = new Date(resolvedBase);
        endOfDay.setHours(23, 59, 59, 999);
        return endOfDay;
    }

    return resolvedBase;
};

export const resolveGameStartDateTime = (game: Game): Date | null => {
    if (!game) return null;

    const directStartKeys = [
        "starts_at",
        "start_at",
        "start_datetime",
        "startDateTime",
        "start_date_time",
        "start_time_iso",
        "start_time_utc",
        "start_timestamp",
        "scheduled_for",
        "scheduled_at",
        "kickoff_at",
        "kick_off_at",
        "appointment_start_at",
    ];

    for (const key of directStartKeys) {
        const parsed = parseDateLike(game[key] as Date | string | number);
        if (parsed) {
            return parsed;
        }
    }

    const dateCandidates = [
        game.appointment_date,
        game.date,
        game.match_date,
        game.start_date,
        game.scheduled_date,
    ].filter(Boolean);

    const timeCandidates = [
        game.appointment_start_time,
        game.start_time,
        game.kickoff_time,
        game.kick_off_time,
        game.time,
        game.match_time,
    ].filter(Boolean);

    for (const dateCandidate of dateCandidates) {
        for (const timeCandidate of timeCandidates) {
            const combined = combineDateAndTime(dateCandidate, timeCandidate);
            if (combined) {
                return combined;
            }
        }
    }

    return null;
};

export const resolveGameEndDateTime = (game: Game): Date | null => {
    if (!game) return null;

    const directEndKeys = [
        "ends_at",
        "end_at",
        "end_datetime",
        "endDateTime",
        "end_date_time",
        "end_time_iso",
        "end_time_utc",
        "finish_at",
        "finish_time",
        "appointment_end_at",
    ];

    for (const key of directEndKeys) {
        const parsed = parseDateLike(game[key] as Date | string | number);
        if (parsed) {
            return parsed;
        }
    }

    const dateCandidates = [
        game.appointment_date,
        game.date,
        game.match_date,
        game.end_date,
        game.scheduled_date,
    ].filter(Boolean);

    const timeCandidates = [
        game.appointment_end_time,
        game.end_time,
        game.finish_time,
        game.kickoff_end_time,
    ].filter(Boolean);

    for (const dateCandidate of dateCandidates) {
        for (const timeCandidate of timeCandidates) {
            const combined = combineDateAndTime(dateCandidate, timeCandidate);
            if (combined) {
                return combined;
            }
        }

        const dateOnly = combineDateAndTime(dateCandidate, null);
        if (dateOnly) {
            return dateOnly;
        }
    }

    return null;
};

const statusCandidates = (game: Game): string[] =>
    [
        game?.appointment_status as string,
        game?.status as string,
        game?.joined_status as string,
        game?.state as string,
        game?.match_status as string,
        game?.game_status as string,
    ]
        .map((value) =>
            typeof value === "string" ? value.trim().toLowerCase() : ""
        )
        .filter(Boolean);

export const nonUpcomingStatuses = new Set([
    "completed",
    "cancelled",
    "canceled",
    "declined",
    "expired",
    "refunded",
    "finished",
    "ended",
]);

export const isGameUpcoming = (game: Game, referenceDate = new Date()): boolean => {
    if (!game) return false;

    const statuses = statusCandidates(game);
    const statusNormalized = statuses.length > 0 ? statuses[0] : "";

    if (statusNormalized && nonUpcomingStatuses.has(statusNormalized)) {
        return false;
    }

    const endDateTime = resolveGameEndDateTime(game);
    if (endDateTime && !Number.isNaN(endDateTime.getTime())) {
        // Treat games that end in the future (or within a minute tolerance) as active/upcoming.
        return endDateTime.getTime() >= referenceDate.getTime() - 60 * 1000;
    }

    const startDateTime = resolveGameStartDateTime(game);
    if (startDateTime && !Number.isNaN(startDateTime.getTime())) {
        // Allow a small tolerance so games starting "now" remain visible.
        const toleranceMs = 5 * 60 * 1000;
        return startDateTime.getTime() >= referenceDate.getTime() - toleranceMs;
    }

    // When we cannot determine a date/time, default to showing unless explicitly completed.
    return statusNormalized !== "completed";
};

const hasGameShape = (item: unknown): boolean => {
    if (!item || typeof item !== "object") return false;
    if (Array.isArray(item)) return item.length > 0;
    return Object.keys(item as Record<string, unknown>).length > 0;
};

export const isGameExpired = (game: Game, referenceDate = new Date()): boolean => {
    if (!hasGameShape(game)) {
        return false;
    }

    return !isGameUpcoming(game, referenceDate);
};
